<template>
  <div>
    <b-container class="wide">
      <!-- Show head-block for the current step -->
      <head-block :title="currentStepTitle" />
      <b-row
        v-if="bookingsInfo.length && !paymentSuccessful"
        class="align-items-center mb-3 mt-2 no-margin-left ml-3 mr-3"
      >
        <b-col>
          <div class="d-flex justify-content-between align-items-center bg-light border rounded px-3 py-2 shadow-sm">
            <!-- Booking timer -->
            <div class="d-flex align-items-center font-weight-bold text-dark">
              <booking-timer
                class="ml-2"
                :bookingsInfo="bookingsInfo"
                @expired="handleBookingExpiration"
              />
            </div>

            <!-- Checkout button or reserved space for invisible button -->
            <div>
              <b-button
                v-if="!selectedBookingsInfo.length && !isBookingExpired"
                size="sm"
                variant="success"
                @click="goToCheckoutDirectly"
              >
                <b-icon icon="credit-card" class="mr-1" />
                {{ $t('pages.new_booking.go_to_checkout') }}
              </b-button>
              <b-button
                v-else
                size="sm"
                variant="light"
                class="invisible"
              >
                <b-icon icon="credit-card" class="mr-1" />
                {{ $t('pages.new_booking.go_to_checkout') }}
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Components based on step -->
      <select-shelf-type-component
        v-if="!selectedShelfType && !selectedShelfBookingPrice && !selectedBookingsInfo.length && shelfTypes && !paymentSuccessful"
        :shelfTypes="shelfTypes"
        :isCustomerBooking="isCustomerBooking"
        @shelfTypeSelected="selectShelfType"
      />
      <select-shelf-period-price-component
        v-if="selectedShelfType && !selectedShelfBookingPrice && !selectedBookingsInfo.length && shelfPeriodPrices.length && feeData && market && !paymentSuccessful"
        :fee="feeData.fee"
        :market="market"
        :shelfPeriodPrices="shelfPeriodPrices"
        :isCustomerBooking="isCustomerBooking"
        @shelfPeriodPriceSelected="selectShelfBookingPrice"
        @backToShelfTypes="goToShelfTypes"
      />
      <select-additional-services-component
        v-if="selectedShelfType && selectedShelfBookingPrice && !selectedBookingsInfo.length && shelfTypeServices.length && !servicesStepCompleted && shelfTypes && market && !paymentSuccessful"
        :shelfTypeServices="shelfTypeServices"
        :isCustomerBooking="isCustomerBooking"
        :market="market"
        @serviceSelected="selectShelfTypeServices"
        @backToShelfPeriodPrices="goToShelfPeriodPrices"
        @skipStep="servicesStepCompleted = true"
      />
      <select-start-date-component
        v-if="selectedShelfType && selectedShelfBookingPrice && !selectedBookingsInfo.length && market && (servicesStepCompleted || !shelfTypeServices.length) && !paymentSuccessful"
        :selectedShelfBookingPrice="selectedShelfBookingPrice"
        :selectedServices="selectedServices"
        :isCustomerBooking="isCustomerBooking"
        :market="market"
        @addMoreBookings="handleAddMoreBookings"
        @deleteBooking="handleDeleteBooking"
        @bookingDateGoBack="bookingDateGoBack"
        @confirmBookings="confirmBookings"
      />
      <booking-checkout-component
        v-if="selectedBookingsInfo.length && market && !paymentSuccessful"
        :market="market"
        :bookingsInfo="selectedBookingsInfo"
        :isCustomerBooking="isCustomerBooking"
        @paymentSuccessful="confirmPayment"
        @backToBookingCalendar="goToBookingCalendar"
      />
      <div class="mt-5 text-secondary" v-if="paymentSuccessful">
        <h3 class="text-center">{{ $t('notifications.text.create_booking.success_booking_confirmed') }}. {{ $t('notifications.text.create_booking.success_countdown_redirection', { countDown: countDown }) }}</h3>
      </div>
    </b-container>
  </div>
</template>

<script>

  import { mapState } from 'vuex'
  import countDownTimerMixin from "@base/mixins/CountDownTimerMixin.vue";
  import { REDIRECT_COUNTDOWN_SECONDS } from "@base/configs";
  import BookingCheckoutComponent from "@base/components/booking/BookingCheckoutComponent.vue";
  import BookingTimer from "@base/components/booking/BookingTimer.vue";
  import SelectShelfPeriodPriceComponent from "@base/components/booking/SelectShelfPeriodPriceComponent.vue";
  import SelectShelfTypeComponent from "@base/components/booking/SelectShelfTypeComponent.vue";
  import SelectAdditionalServicesComponent from "@base/components/booking/SelectAdditionalServicesComponent.vue";
  import SelectStartDateComponent from "@base/components/booking/SelectStartDateComponent.vue";
  import HeadBlock from "@base/components/HeadBlock.vue";

  export default {
    name: 'CreateBookingView',
    components: {
      HeadBlock,
      BookingTimer,
      BookingCheckoutComponent,
      SelectShelfPeriodPriceComponent,
      SelectShelfTypeComponent,
      SelectStartDateComponent,
      SelectAdditionalServicesComponent
    },

    mixins: [countDownTimerMixin],

    data () {
      return {
        loading: false,
        selectedShelfType: null,
        selectedShelfBookingPrice: null,
        selectedServices: [],
        servicesStepCompleted: false,
        selectedStartDate: null,
        bookingsInfo: [],
        selectedBookingsInfo: [],
        isBookingExpired: false,
        paymentSuccessful: false,
        countDown: REDIRECT_COUNTDOWN_SECONDS,
      }
    },

    created () {
      this.getShelfTypes()
      this.getBookingsInfo()
    },

    props: {
      isCustomerBooking: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      ...mapState({
      shelfPeriodPrices: state => state.shelfPeriodPrices,
      shelfTypes: state => state.shelfTypes,
      shelfTypeServices: state => state.shelfTypeServices,
      feeData () {
        return this.isCustomerBooking
          ? this.$store.state.customerData
          : this.$store.state.anonMarket
      },
      market () {
        return this.isCustomerBooking
          ? this.$store.state.userMarket
          : this.$store.state.anonMarket
        }
      }),
      currentStepTitle () {
        if (!this.selectedShelfType && !this.selectedShelfBookingPrice && !this.selectedBookingsInfo.length && !this.paymentSuccessful) {
          return this.$t('title.customer.new_booking_1')
        } else if (this.selectedShelfType && !this.selectedShelfBookingPrice && !this.selectedBookingsInfo.length && !this.paymentSuccessful) {
          return this.$t('title.customer.new_booking_2_period_prices')
        } else if (this.selectedShelfType && this.selectedShelfBookingPrice && !this.selectedBookingsInfo.length && !this.servicesStepCompleted && this.shelfTypeServices.length && this.shelfTypes && !this.paymentSuccessful) {
          return this.$t('title.customer.new_booking_2_services')
        } else if (this.selectedShelfType && this.selectedShelfBookingPrice && !this.selectedBookingsInfo.length && (this.servicesStepCompleted || !this.shelfTypeServices.length) && !this.paymentSuccessful) {
          return this.$t('title.customer.new_booking_3')
        } else if (this.selectedBookingsInfo.length && !this.paymentSuccessful) {
          return this.$t('title.customer.new_booking_4')
        }
        return ''
      }
    },

    methods: {
      getShelfTypes () {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          const action = (this.isCustomerBooking) ? this.$_actionTypes.GET_SHELF_TYPES :
          this.$_actionTypes.GET_SHELF_TYPES_ANON
          this.$store.dispatch(action)
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
              resolve()
            })
        })
      },

      getShelfPeriodPrices () {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          const action = (this.isCustomerBooking) ? this.$_actionTypes.GET_SHELF_PERIOD_PRICES :
          this.$_actionTypes.GET_SHELF_PERIOD_PRICES_ANON
          this.$store.dispatch(action, this.selectedShelfType.id)
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
              resolve()
            })
        })
      },

      getAdditionalServices () {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          const action = (this.isCustomerBooking) ? this.$_actionTypes.GET_SHELF_TYPE_SERVICES :
          this.$_actionTypes.GET_SHELF_TYPE_SERVICES_ANON
          this.$store.dispatch(action, this.selectedShelfType.id)
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
              resolve()
            })
        })
      },

      selectShelfType (shelfTypeObj) {
        this.selectedShelfType = shelfTypeObj
        if (this.isBookingExpired) {
          this.bookingsInfo = []
          this.isBookingExpired = false
        }
        this.getShelfPeriodPrices()
      },
      selectShelfBookingPrice (shelfPeriodPriceObj) {
        this.selectedShelfBookingPrice = shelfPeriodPriceObj
        this.getAdditionalServices()
      },

      selectShelfTypeServices (serviceList) {
        this.selectedServices = serviceList
        this.servicesStepCompleted = true
        if (this.isBookingExpired) {
          this.bookingsInfo = []
          this.isBookingExpired = false
        }
      },

      goToShelfTypes () {
        this.selectedShelfType = null
        this.selectedShelfBookingPrice = null
        this.selectedStartDate = null
        this.selectedBookingsInfo = []
      },

      handleAddMoreBookings (bookingsInfo) {
        this.bookingsInfo = bookingsInfo
        this.selectedShelfType = null
        this.selectedShelfBookingPrice = null
        this.servicesStepCompleted = false
        this.selectedServices = []
        this.selectedStartDate = null
      },

      goToShelfPeriodPrices () {
        this.selectedShelfBookingPrice = null
        this.selectedStartDate = null
      },

      bookingDateGoBack () {
        this.selectedServices = []
        this.servicesStepCompleted = false
        if (!this.shelfTypeServices.length) {
          this.selectedShelfBookingPrice = null
          this.selectedStartDate = null
        }
      },

      goToBookingCalendar () {
        this.selectedBookingsInfo = []
      },

      confirmBookings (objs) {
        this.selectedBookingsInfo = objs
        this.bookingsInfo = objs
      },

      confirmPayment () {
        this.paymentSuccessful = true
        this.handleBookingExpiration()
        this.bookingsInfo = []
        this.selectedBookingsInfo = []
        if (this.isCustomerBooking) {
          this.countDownTimer(this.countDown, this, { name: 'sales' })
        } else {
          this.countDownTimer(this.countDown, this, { name: 'user-login', params: this.$route.params.marketId })
        }
      },

      handleBookingExpiration () {
        localStorage.removeItem("bookingsInfo")
        localStorage.removeItem("cartToken")
        localStorage.removeItem("paymentId")
        this.selectedShelfType = null
        this.selectedShelfBookingPrice = null
        this.selectedStartDate = null
        this.selectedBookingsInfo = []
        this.servicesStepCompleted = false
        this.selectedServices = []
        this.isBookingExpired = true
      },

      handleDeleteBooking (bookingsInfo) {
        this.bookingsInfo = bookingsInfo
      },

      getBookingsInfo () {
        this.bookingsInfo = []
        const storageValue = localStorage.getItem("bookingsInfo")
        if (!storageValue) {
          return []
        }
       const bookingsInfo = JSON.parse(localStorage.getItem("bookingsInfo"))
        const now = new Date()
        for (const obj of bookingsInfo) {
          // compare the expiry time of the item with the current time
          if (now.getTime() < obj.expiresOn) {
            // If the item is expired, delete the item from storage
            // and return null
            this.bookingsInfo.push(obj)
          }
        }
        localStorage.setItem("bookingsInfo", JSON.stringify(this.bookingsInfo))
      },

      goToCheckoutDirectly () {
        this.selectedBookingsInfo = this.bookingsInfo
      },
    }
  }
</script>

<style scoped>

</style>